import { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { visuallyHidden } from "@mui/utils";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import positionApi from "../../api/positionApi";
import structureApi from "../../api/structureApi";
import boardTypeApi from "../../api/boardTypeApi";
import PositionModal from "../../components/common/PositionModal";
import BoardTypeModal from "../../components/common/BoardTypeModal";
import assets from "../../assets/index";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "@mui/material/styles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import imageCompression from "browser-image-compression";
import heic2any from "heic2any";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

let timer;
const timeout = 500;

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: true,
    label: "Name",
  },
  {
    id: "boardType",
    numeric: false,
    disablePadding: true,
    label: "Division",
  },
  {
    id: "action",
    numeric: true,
    disablePadding: false,
    label: "Action",
  },
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const {
    numSelected,
    showDialogDeletePosition,
    selected,
    requestSearch,
    searchFilter,
  } = props;
  const theme = useTheme();

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{
            flex: "1 1 100%",
            display: "flex",
            alignItems: "center",
            color:
              theme.palette.mode === "dark"
                ? assets.colors.rolesTextDark
                : assets.colors.rolesTextLight,
          }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          <FormatListBulletedIcon sx={{ mr: 1 }} /> Position
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton onClick={() => showDialogDeletePosition(selected)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <TextField
          sx={{
            background:
              theme.palette.mode === "dark"
                ? assets.colors.inputTypeDark
                : assets.colors.inputTypeLight,
            mt: 2,
            mr: 2,
            width: "300px",
          }}
          value={searchFilter}
          size="small"
          margin="normal"
          variant="outlined"
          placeholder="Search name..."
          type="search"
          onInput={(e) => requestSearch(e.target.value)}
        />
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const Positions = () => {
  const user = useSelector((state) => state.user.value);
  const theme = useTheme();
  const [selectedPosition, setSelectedPosition] = useState(undefined);
  const [selectedBoardType, setSelectedBoardType] = useState(undefined);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const dense = useState(false);
  const [rows, setRows] = useState([]);
  const [boardTypeRows, setBoardTypeRows] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [structure, setStructure] = useState("");
  const [boardTypes, setBoardTypes] = useState([]);

  const [fileURL, setFileURL] = useState(null);

  const [loading, setLoading] = useState(false);
  const [deleteFailCount, setDeleteFailCount] = useState(0);
  const [deleteFailCountBoardType, setDeleteFailCountBoardType] = useState(0);
  const [nameErrText, setNameErrText] = useState("");

  const [nameBoardTypeErrText, setNameBoardTypeErrText] = useState("");

  const [copyList, setCopyList] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const requestSearch = (searched) => {
    setCopyList(
      rows.filter((item) =>
        item.name.toString().toLowerCase().includes(searched)
      )
    );
    setSearchFilter(searched);
  };

  const onUpdatePosition = (position) => {
    const newData = [...rows];
    const positionIndex = newData.findIndex((e) => e._id === position.id);
    newData[positionIndex].name = position.name;
    newData[positionIndex].boardType = position.boardType;
    setRows(newData);
    setCopyList(newData);
  };

  const onUpdateBoardType = (boardType) => {
    const newData = [...boardTypeRows];
    const colIndex = newData.findIndex((e) => e._id === boardType.id);
    newData[colIndex].name = boardType.name;
    setBoardTypeRows(newData);
  };

  const deleteBoardType = (boardType) => {
    timer = setTimeout(async () => {
      try {
        const deleteJson = await boardTypeApi.delete(boardType);
        if (deleteJson === "deleted") {
          const newData = [...boardTypeRows].filter((e) => e.id !== boardType);
          setBoardTypeRows(newData);
          hideDialog();
        } else {
          setDeleteFailCountBoardType(deleteJson);
          setOpenDialogDeleteFailBoardType(true);
        }
      } catch (err) {
        console.log(err);
      }
    }, timeout);
  };

  const deletePositions = (positions) => {
    positions.forEach(async (value) => {
      try {
        const deleteJson = await positionApi.delete(value);
        if (deleteJson === "deleted") {
          const newData = [...rows].filter((e) => !positions.includes(e.id));
          setRows(newData);
          setCopyList(newData);
          setSelected([]);
          hideDialog();
        } else {
          setDeleteFailCount(deleteJson);
          setOpenDialogDeleteFail(true);
        }
      } catch (err) {
        console.log(err);
      }
    });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    const getPositions = async () => {
      try {
        const jsonData = [];
        const res = await positionApi.getAll();
        res.forEach((value, index) => {
          jsonData.push({
            _id: value.id,
            id: value.id,
            name: value.name,
            boardType: value.boardType,
            action: (
              <Tooltip title="Edit">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPosition(value);
                  }}
                >
                  <ModeEditOutlineOutlinedIcon />
                </IconButton>
              </Tooltip>
            ),
          });
        });
        setRows(jsonData);
        setCopyList(jsonData);
      } catch (err) {
        console.log(err);
      }
    };
    getPositions();

    const getStructure = async () => {
      try {
        const res = await structureApi.getAll();
        setStructure(res.image);
      } catch (err) {
        console.log(err);
      }
    };
    getStructure();

    const getBoardTypes = async () => {
      try {
        const res = await boardTypeApi.getAll();
        setBoardTypes(res);
        let jsonData = [];
        res.forEach((value, index) => {
          jsonData.push({
            _id: value.id,
            id: value.id,
            name: value.name,
            boardType: value.boardType,
            action: (
              <>
                <Tooltip title="Edit">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBoardType(value);
                    }}
                  >
                    <ModeEditOutlineOutlinedIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      showDialogDeleteBoardType(value.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            ),
          });
        });
        setBoardTypeRows(jsonData);
      } catch (err) {
        console.log(err);
      }
    };
    getBoardTypes();
  }, []);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, rows]
  );

  const visibleRowsCopy = useMemo(
    () =>
      stableSort(copyList, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, copyList]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNameErrText("");

    const data = new FormData(e.target);
    const name = data.get("name").trim();
    const boardType = data.get("boardType").trim();

    console.log(boardType);
    let err = false;

    if (name === "") {
      err = true;
      setNameErrText("Please fill this field");
    }

    if (err) return;

    setLoading(true);

    try {
      let res = [];
      if (boardType !== "") {
        res = await positionApi.create({
          name,
          boardType,
        });
      } else {
        res = await positionApi.create({
          name,
        });
      }

      setLoading(false);
      const boardTypeIndex = boardTypes.findIndex(
        (e) => e.id === res.boardType
      );
      res.boardType = boardTypes[boardTypeIndex];
      const newJsonData = {
        _id: res.id,
        id: res.id,
        name: res.name,
        boardType: res.boardType,
        action: (
          <Tooltip title="Edit">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPosition(res);
              }}
            >
              <ModeEditOutlineOutlinedIcon />
            </IconButton>
          </Tooltip>
        ),
      };
      const newData = [...rows];
      newData.unshift(newJsonData);
      console.log(newData);
      setRows(newData);
      setCopyList(newData);
      e.target.reset();
    } catch (err) {
      console.log(err);
      const errors = err.data.errors;
      errors.forEach((e) => {
        if (e.param === "name") {
          setNameErrText(e.msg);
        }
      });
      setLoading(false);
    }
  };

  const handleSubmitBoardType = async (e) => {
    e.preventDefault();
    setNameBoardTypeErrText("");

    const data = new FormData(e.target);
    const name = data.get("name").trim();

    let err = false;

    if (name === "") {
      err = true;
      setNameBoardTypeErrText("Please fill this field");
    }

    if (err) return;

    setLoading(true);

    try {
      const res = await boardTypeApi.create({
        name,
      });
      setLoading(false);
      const newJsonData = {
        _id: res.id,
        id: res.id,
        name: res.name,
        action: (
          <>
            <Tooltip title="Edit">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBoardType(res);
                }}
              >
                <ModeEditOutlineOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  showDialogDeleteBoardType(res.id);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </>
        ),
      };
      const newData = [...boardTypeRows];
      newData.unshift(newJsonData);
      setBoardTypeRows(newData);
      e.target.reset();
    } catch (err) {
      console.log(err);
      const errors = err.data.errors;
      errors.forEach((e) => {
        if (e.param === "name") {
          setNameErrText(e.msg);
        }
      });
      setLoading(false);
    }
  };

  const [openDialogDeletePosition, setOpenDialogDeletePosition] =
    useState(false);
  const [openDialogDeleteFail, setOpenDialogDeleteFail] = useState(false);
  const [idDeletePosition, setIdDeletePosition] = useState(false);

  const [openDialogDeleteBoardType, setOpenDialogDeleteBoardType] =
    useState(false);
  const [openDialogDeleteFailBoardType, setOpenDialogDeleteFailBoardType] =
    useState(false);
  const [idDeleteBoardType, setIdDeleteBoardType] = useState(false);

  const showDialogDeletePosition = (sectionId) => {
    setOpenDialogDeletePosition(true);
    setIdDeletePosition(sectionId);
  };

  const showDialogDeleteBoardType = (sectionId) => {
    setOpenDialogDeleteBoardType(true);
    setIdDeleteBoardType(sectionId);
  };

  const hideDialog = () => {
    setOpenDialogDeletePosition(false);
    setOpenDialogDeleteFail(false);
    setOpenDialogDeleteBoardType(false);
    setOpenDialogDeleteFailBoardType(false);
  };

  const uploadImage = async (e) => {
    const newName = e.target.files[0];
    if (newName.type === "image/heic") {
      convertAndCompressHeic(newName);
    } else {
      compressNonHeic(newName);
    }
  };

  async function getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
    });
  }

  const updateImage = async (e) => {
    clearTimeout(timer);
    const image = await getBase64(fileURL)
      .then((res) => res)
      .catch((err) => err);
    timer = setTimeout(async () => {
      try {
        await structureApi.create({
          image,
        });
      } catch (err) {
        console.log(err);
      }
    }, timeout);
    setStructure(image);
    setFileURL(null);
  };

  const convertAndCompressHeic = async (file) => {
    const blob = file;
    try {
      setLoading(true);
      const convertedBlob = await heic2any({
        blob,
        toType: "image/jpeg",
        quality: 0.5,
      });

      const convertedFile = new File([convertedBlob], file.name, {
        type: convertedBlob.type,
      });

      setFileURL(convertedFile);

      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  const compressNonHeic = async (file) => {
    try {
      setLoading(true);
      const compressedBlob = await imageCompression(file, {
        maxSizeMB: 1,
        // maxWidthOrHeight: 100,
        useWebWorker: true,
      });

      const convertedFile = new File([compressedBlob], file.name, {
        type: compressedBlob.type,
      });

      setFileURL(convertedFile);
      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      {openDialogDeleteFail && (
        <Dialog
          open={openDialogDeleteFail}
          onClose={hideDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              There are still {deleteFailCount} users registered in this
              position, please check user management
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={hideDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {openDialogDeletePosition && (
        <Dialog
          open={openDialogDeletePosition}
          onClose={hideDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure delete position
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => deletePositions(idDeletePosition)}
              color="primary"
            >
              Yes
            </Button>
            <Button onClick={hideDialog} color="primary">
              No
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {openDialogDeleteFailBoardType && (
        <Dialog
          open={openDialogDeleteFailBoardType}
          onClose={hideDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              There are still {deleteFailCountBoardType} position registered in
              this division
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={hideDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {openDialogDeleteBoardType && (
        <Dialog
          open={openDialogDeleteBoardType}
          onClose={hideDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure delete division
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => deleteBoardType(idDeleteBoardType)}
              color="primary"
            >
              Yes
            </Button>
            <Button onClick={hideDialog} color="primary">
              No
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {user.role.isManage === 1 && (
        <>
          <Box
            sx={{
              // width: "100%",
              margin: "5px 25px 0px 25px",
            }}
          >
            <Typography
              sx={{
                padding: 0,
                fontSize: "1.5rem",
                fontWeight: "500",
                color:
                  theme.palette.mode === "dark"
                    ? assets.colors.rolesTextDark
                    : assets.colors.rolesTextLight,
              }}
            >
              🏢 Company Structure
            </Typography>
            <Typography
              sx={{
                padding: 0,
                marginTop: "2px",
                fontSize: "14px",
                fontWeight: "400",
                color:
                  theme.palette.mode === "dark"
                    ? assets.colors.rolesTextDark
                    : assets.colors.rolesTextLight,
              }}
            >
              <i>
                Manage all job positions dan company structure in the zabran
                scrum board application
              </i>
            </Typography>
          </Box>
          <Box
            sx={{
              // width: "100%",
              margin: "0px 10px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Grid container spacing={0}>
              <Grid item xs={12} sm={4}>
                <Paper
                  sx={{
                    m: 2,
                    p: 2,
                    border:
                      theme.palette.mode === "dark"
                        ? "1px solid " + assets.colors.borderDark
                        : "1px solid " + assets.colors.borderLight,
                  }}
                >
                  <Typography
                    sx={{
                      flex: "1 1 100%",
                      display: "flex",
                      alignItems: "center",
                      color:
                        theme.palette.mode === "dark"
                          ? assets.colors.rolesTextDark
                          : assets.colors.rolesTextLight,
                    }}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                  >
                    <PersonAddIcon sx={{ mr: 1 }} /> Add Job Position
                  </Typography>
                  <Box
                    component="form"
                    sx={{ mt: 1 }}
                    onSubmit={handleSubmit}
                    noValidate
                  >
                    <Typography sx={{ color: "rgb(108, 108, 108)" }}>
                      Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        gap: "1px",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <TextField
                        size="small"
                        margin="normal"
                        required
                        fullWidth
                        id="name"
                        name="name"
                        disabled={loading}
                        error={nameErrText !== ""}
                        helperText={nameErrText}
                        sx={{
                          background:
                            theme.palette.mode === "dark"
                              ? assets.colors.inputTypeDark
                              : assets.colors.inputTypeLight,
                          mt: 0,
                        }}
                      />
                    </Box>

                    <Typography sx={{ mt: 1, color: "rgb(108, 108, 108)" }}>
                      Select Division <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        gap: "1px",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <FormControl
                        size="small"
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-input": {
                            padding: "8.5px 0px 8.5px 8.5px",
                          },
                          "& .MuiFormControl-root": {
                            background:
                              theme.palette.mode === "dark"
                                ? assets.colors.inputTypeDark
                                : assets.colors.inputTypeLight,
                          },
                          "& .MuiOutlinedInput-root": {
                            background:
                              theme.palette.mode === "dark"
                                ? assets.colors.inputTypeDark
                                : assets.colors.inputTypeLight,
                          },
                        }}
                      >
                        <Select
                          labelId="label-position"
                          name="boardType"
                          fullWidth
                          id="boardType"
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-input": {
                              padding: "8.5px 0px 8.5px 8.5px",
                            },
                            "& .MuiFormControl-root": {
                              background:
                                theme.palette.mode === "dark"
                                  ? assets.colors.inputTypeDark
                                  : assets.colors.inputTypeLight,
                            },
                            "& .MuiOutlinedInput-root": {
                              background:
                                theme.palette.mode === "dark"
                                  ? assets.colors.inputTypeDark
                                  : assets.colors.inputTypeLight,
                            },
                            "& .MuiInputBase-root": {
                              background:
                                theme.palette.mode === "dark"
                                  ? assets.colors.inputTypeDark
                                  : assets.colors.inputTypeLight,
                            },
                          }}
                        >
                          {boardTypes.map((boardType, index) => (
                            <MenuItem key={index} value={boardType.id}>
                              {boardType.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <LoadingButton
                      sx={{ mt: 3, mb: 2 }}
                      fullWidth
                      variant="contained"
                      color="primary"
                      type="submit"
                      loading={loading}
                    >
                      <PersonAddIcon sx={{ mr: 1 }} /> Create Position
                    </LoadingButton>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Paper
                  sx={{
                    m: 2,
                    border:
                      theme.palette.mode === "dark"
                        ? "1px solid " + assets.colors.borderDark
                        : "1px solid " + assets.colors.borderLight,
                  }}
                >
                  <EnhancedTableToolbar
                    numSelected={selected.length}
                    showDialogDeletePosition={showDialogDeletePosition}
                    requestSearch={requestSearch}
                    searchFilter={searchFilter}
                    selected={selected}
                  />
                  <TableContainer>
                    <Table
                      sx={{ minWidth: 750 }}
                      aria-labelledby="tableTitle"
                      size={dense ? "small" : "medium"}
                    >
                      <EnhancedTableHead
                        numSelected={selected.length}
                        order={order}
                        orderBy={orderBy}
                        onSelectAllClick={handleSelectAllClick}
                        onRequestSort={handleRequestSort}
                        rowCount={rows.length}
                      />
                      <TableBody>
                        {(visibleRowsCopy.length > 0
                          ? visibleRowsCopy
                          : visibleRows
                        ).map((row, index) => {
                          const isItemSelected = isSelected(row.id);
                          const labelId = `enhanced-table-checkbox-${index}`;

                          return (
                            <TableRow
                              hover
                              onClick={(event) => handleClick(event, row.id)}
                              role="checkbox"
                              aria-checked={isItemSelected}
                              tabIndex={-1}
                              key={row.id}
                              selected={isItemSelected}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  color="primary"
                                  checked={isItemSelected}
                                  inputProps={{
                                    "aria-labelledby": labelId,
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                component="th"
                                id={labelId}
                                scope="row"
                                padding="none"
                              >
                                {row.name}
                              </TableCell>
                              <TableCell
                                component="th"
                                id={labelId}
                                scope="row"
                                padding="none"
                              >
                                {row.boardType && row.boardType.name}
                              </TableCell>

                              <TableCell align="right">{row.action}</TableCell>
                            </TableRow>
                          );
                        })}
                        {emptyRows > 0 && (
                          <TableRow
                            style={{
                              height: (dense ? 33 : 53) * emptyRows,
                            }}
                          >
                            <TableCell colSpan={6} />
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              // width: "100%",
              margin: "0px 10px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Grid container spacing={0}>
              <Grid item xs={12} sm={4}>
                <Paper
                  sx={{
                    m: 2,
                    p: 2,
                    border:
                      theme.palette.mode === "dark"
                        ? "1px solid " + assets.colors.borderDark
                        : "1px solid " + assets.colors.borderLight,
                  }}
                >
                  <Typography
                    sx={{
                      flex: "1 1 100%",
                      display: "flex",
                      alignItems: "center",
                      color:
                        theme.palette.mode === "dark"
                          ? assets.colors.rolesTextDark
                          : assets.colors.rolesTextLight,
                    }}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                  >
                    <PersonAddIcon sx={{ mr: 1 }} /> Add Division
                  </Typography>
                  <Box
                    component="form"
                    sx={{ mt: 1 }}
                    onSubmit={handleSubmitBoardType}
                    noValidate
                  >
                    <Typography sx={{ color: "rgb(108, 108, 108)" }}>
                      Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        gap: "1px",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <TextField
                        size="small"
                        margin="normal"
                        required
                        fullWidth
                        id="name"
                        name="name"
                        disabled={loading}
                        error={nameBoardTypeErrText !== ""}
                        helperText={nameBoardTypeErrText}
                        sx={{
                          background:
                            theme.palette.mode === "dark"
                              ? assets.colors.inputTypeDark
                              : assets.colors.inputTypeLight,
                          mt: 0,
                        }}
                      />
                    </Box>

                    <LoadingButton
                      sx={{ mt: 3, mb: 2 }}
                      fullWidth
                      variant="contained"
                      color="primary"
                      type="submit"
                      loading={loading}
                    >
                      <PersonAddIcon sx={{ mr: 1 }} /> Create Division
                    </LoadingButton>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={8}>
                <Paper
                  sx={{
                    m: 2,
                    border:
                      theme.palette.mode === "dark"
                        ? "1px solid " + assets.colors.borderDark
                        : "1px solid " + assets.colors.borderLight,
                  }}
                >
                  <Typography
                    sx={{
                      mt: 2,
                      ml: 2,
                      mb: 2,
                      mr: 2,
                      flex: "1 1 100%",
                      display: "flex",
                      alignItems: "center",
                      color:
                        theme.palette.mode === "dark"
                          ? assets.colors.rolesTextDark
                          : assets.colors.rolesTextLight,
                    }}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                  >
                    <FormatListBulletedIcon sx={{ mr: 1 }} /> Division
                  </Typography>
                  <TableContainer>
                    <Table
                      sx={{ minWidth: 750 }}
                      aria-labelledby="tableTitle"
                      size={dense ? "small" : "medium"}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {boardTypeRows.map((row, index) => {
                          const labelId = `enhanced-table-checkbox-division-${index}`;

                          return (
                            <TableRow hover key={row.id}>
                              <TableCell
                                sx={{ pl: 2 }}
                                component="th"
                                id={labelId}
                                scope="row"
                              >
                                {row.name}
                              </TableCell>
                              <TableCell align="right">{row.action}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
      <Box
        sx={{
          // width: "100%",
          margin: "0 10px 10px 10px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            m: 2,
            p: 2,
            border:
              theme.palette.mode === "dark"
                ? "1px solid " + assets.colors.borderDark
                : "1px solid " + assets.colors.borderLight,
          }}
        >
          <Box
            sx={{
              width: "100%",
              margin: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                flex: "1 1 100%",
                display: "flex",
                alignItems: "center",
                color:
                  theme.palette.mode === "dark"
                    ? assets.colors.rolesTextDark
                    : assets.colors.rolesTextLight,
              }}
              variant="h6"
              id="tableTitle"
              component="div"
            >
              <AccountTreeIcon sx={{ mr: 1 }} />
              Company Structure
            </Typography>
            {user.role.isManage === 1 && (
              <Box>
                Upload Image
                <Button
                  disableElevation={true}
                  variant="contained"
                  sx={{
                    background:
                      theme.palette.mode === "dark"
                        ? assets.colors.inputTypeDark
                        : assets.colors.inputTypeLight,
                    border:
                      theme.palette.mode === "dark"
                        ? "1px solid " + assets.colors.borderDark
                        : "1px solid " + assets.colors.borderLight,
                    color: "#999",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? assets.colors.inputTypeDark
                          : assets.colors.inputTypeLight,
                    },
                  }}
                  component="label"
                >
                  <CloudUploadIcon sx={{ ml: 1, mr: 1, fontSize: "22px" }} />{" "}
                  Upload
                  <input
                    type="file"
                    name="image"
                    disabled={loading}
                    accept="image/*"
                    onChange={uploadImage}
                    required
                    hidden
                  />
                </Button>
                {fileURL && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ paddingTop: "5px", mt: 1 }}
                    onClick={updateImage}
                  >
                    Update Image
                  </Button>
                )}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              width: "100%",
            }}
          >
            <img src={structure} alt="structure" style={{ width: "100%" }} />
          </Box>
        </Paper>
      </Box>

      <PositionModal
        position={selectedPosition}
        boardTypes={boardTypes}
        onClose={() => setSelectedPosition(undefined)}
        onUpdate={onUpdatePosition}
      />

      <BoardTypeModal
        boardType={selectedBoardType}
        onClose={() => setSelectedBoardType(undefined)}
        onUpdate={onUpdateBoardType}
      />
    </>
  );
};

export default Positions;
